import type { Dispatch, RefObject, SetStateAction } from 'react'
import type { IEpisode, IPlaylist, ISeason } from './Playlist.h'

import { PlaylistPopoverMenuItems } from './Playlist.h'

export const getActiveEpisode = (
  episodes?: IEpisode[],
  episodeId?: number | null
): IEpisode | undefined => {
  return episodes?.find((episode) => episode.id === episodeId)
}

export const getActiveSeason = ({
  seasonsWithEpisodes,
  currentEpisode,
}: {
  seasonsWithEpisodes?: ISeason[]
  currentEpisode?: number | null
}): ISeason | undefined => {
  return seasonsWithEpisodes?.find((season) =>
    getActiveEpisode(season.episodes, currentEpisode)
  )
}

export const scrollToContentItem = <T extends { id?: number }>({
  contentItems,
  activeContentItem,
  popupBodyRef,
}: {
  contentItems?: T[]
  activeContentItem?: T
  popupBodyRef: RefObject<HTMLDivElement | null>
}) => {
  if (!contentItems) return
  const activeId = contentItems.findIndex(
    ({ id }) => id === activeContentItem?.id
  )
  const scrollTo = Math.max(activeId, 0)

  popupBodyRef.current?.children[scrollTo]?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  })
}

export const isActiveTabSeasons = ({
  isSeasons,
  activeTab,
}: {
  isSeasons: boolean
  activeTab: PlaylistPopoverMenuItems
}): boolean =>
  activeTab === PlaylistPopoverMenuItems.Default ||
  (activeTab === PlaylistPopoverMenuItems.Seasons && isSeasons)

export const isActiveTabEpisodes = ({
  isSeasons,
  activeTab,
}: {
  isSeasons: boolean
  activeTab: PlaylistPopoverMenuItems
}): boolean =>
  activeTab === PlaylistPopoverMenuItems.Episodes ||
  (activeTab === PlaylistPopoverMenuItems.Default && !isSeasons)

export const setFullContent = ({
  seasonsWithEpisodes,
  currentEpisode,
  setActiveSeason,
  setActiveEpisode,
}: {
  seasonsWithEpisodes?: ISeason[]
  currentEpisode?: number | null
  setActiveEpisode: Dispatch<SetStateAction<IEpisode | undefined>>
  setActiveSeason: Dispatch<SetStateAction<ISeason | undefined>>
}) => {
  const activeSeason = getActiveSeason({ seasonsWithEpisodes, currentEpisode })
  const activeEpisode = getActiveEpisode(activeSeason?.episodes, currentEpisode)
  if (activeEpisode && activeSeason) {
    setActiveEpisode(activeEpisode)
    setActiveSeason(activeSeason)
  }
}

export const getPlaylistContent = ({
  isSeasons,
  playlist,
  isTabSeasons,
  activeSeason,
}: {
  activeSeason?: ISeason
  isSeasons: boolean
  isTabSeasons: boolean
  playlist?: IPlaylist
}): ISeason[] | IEpisode[] | undefined => {
  const { seasonsWithEpisodes, episodes } = playlist || {}

  if (isTabSeasons && isSeasons) {
    return seasonsWithEpisodes
  }
  if (isTabSeasons && !isSeasons) {
    return episodes
  }
  return activeSeason?.episodes
}

export const formatProgress = ({
  duration,
  stoppedTime,
  watched,
}: {
  duration?: number | null
  stoppedTime?: number | null
  watched?: boolean
}): number | undefined => {
  if (!stoppedTime && watched) return 100

  if (!duration || !stoppedTime) return

  return (stoppedTime * 100) / duration
}

export const getPlaylistDurationInfo = (item: ISeason | IEpisode) => {
  return {
    length: undefined,
    duration: undefined,
    stoppedTime: undefined,
    watched: undefined,
    ...item,
  }
}
